import { useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import type { AnalysisTemplate } from "@/api/deals-api";
import { analysisTemplatesApi } from "@/api/deals-api";
import { getErrorMessage } from "@/lib/api-error";

/**
 * Describe the analysis you want; get a draft to edit.
 *
 * A blank prompt box is the hardest thing in this feature to fill in — knowing
 * that a section's brief has to be answerable from a company's own documents is
 * the whole skill, and it is not obvious from an empty textarea.
 *
 * The result is **not saved**. It lands in the editor as an unsaved analysis for
 * the fund to change and save themselves, because a template nobody read would
 * silently shape every memo they ever produce.
 */

/** Examples, because the quality of the draft follows the description. */
const EXAMPLES = [
  "A fast screening note for pre-seed deals — what they do, whether there's real signal, what worries me, and what to ask the founder.",
  "A full IC memo: summary and recommendation, problem and product, market with sizing, traction with the metrics in a table, team, competition, and the risks we'd be taking.",
  "A technical diligence read for infrastructure companies — architecture, what they've actually built versus what they claim, scalability, and key engineering risks.",
];

interface DraftAnalysisDialogProps {
  workspaceId: string;
  /** Called with the drafted analysis, for the caller to add unsaved. */
  onDrafted: (template: AnalysisTemplate) => void;
  disabled?: boolean;
}

export function DraftAnalysisDialog({
  workspaceId,
  onDrafted,
  disabled,
}: DraftAnalysisDialogProps) {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const draft = async () => {
    const trimmed = description.trim();
    if (!trimmed || pending) return;

    setPending(true);
    setError(null);
    try {
      const template = await analysisTemplatesApi.draft(workspaceId, trimmed);
      onDrafted(template);
      setOpen(false);
      setDescription("");
    } catch (draftError) {
      // Shown in the dialog rather than as a toast: the description is right
      // there to change, and "try describing the sections in more detail" is
      // only useful next to the text it is about.
      setError(getErrorMessage(draftError, "Couldn't draft that agent"));
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setError(null);
      }}
    >
      <Button
        size="sm"
        variant="outline"
        className="h-8 gap-1.5 text-xs"
        onClick={() => setOpen(true)}
        disabled={disabled}
      >
        <Sparkles className="h-3.5 w-3.5 text-primary" />
        Draft with AI
      </Button>

      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4 text-primary" />
            Draft an agent
          </DialogTitle>
          <DialogDescription className="text-xs">
            Describe what you want the agent to cover, in your own words. Say
            which sections you want and what each should answer. You&rsquo;ll get a
            draft to edit — nothing is saved until you save it.
          </DialogDescription>
        </DialogHeader>

        <Textarea
          autoFocus
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="e.g. A screening note for seed deals: what they do, the signal, my concerns, and what to ask the founder."
          className="min-h-[120px] text-sm"
        />

        <div className="space-y-1.5">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Or start from an example
          </p>
          {EXAMPLES.map((example) => (
            <button
              key={example}
              type="button"
              disabled={pending}
              onClick={() => setDescription(example)}
              className="block w-full rounded border border-border px-2.5 py-1.5 text-left text-xs text-muted-foreground transition-colors hover:border-primary hover:text-foreground disabled:opacity-50"
            >
              {example}
            </button>
          ))}
        </div>

        {error && <p className="text-xs text-destructive">{error}</p>}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setOpen(false)}
            disabled={pending}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="gap-1.5"
            disabled={!description.trim() || pending}
            onClick={() => void draft()}
          >
            {pending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            {pending ? "Drafting…" : "Draft it"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
